// Every address on a Videas host that the app sends somebody to, in one place: the pages of the
// corporate site (videas.com — the help centre, the blog, pricing, the free tools) and the demo
// videos played from app.videas.com. A link is checked, changed or translated here and nowhere
// else; a component names a key and never writes a URL. `bun run check:links` requests every one.
//
// **One list per language, with the same keys.** The corporate site spells each page in its
// reader's language — the English page of the player settings article is
// `/en/helpdesk/video-player-settings/`, not the French slug under `/en/` — so a path is written
// out whole in both lists rather than assembled from a shared slug. `satisfies` makes a key
// missing from either list a type error.
//
// The paths are the site's whichever host serves it: the origin is `VITE_CORPORATE_URL`,
// `https://videas.com` when unset. The demo videos are not the site's and carry their own host.
// The site's JSON feeds are read from the same origin, by `src/api/corporate/`.

/** The two languages the corporate site publishes — the app's two locales. */
export type VideasLinkLocale = 'fr' | 'en'

/** The corporate site's origin, without a trailing slash. */
export const CORPORATE_ORIGIN = (import.meta.env.VITE_CORPORATE_URL || 'https://videas.com').replace(/\/+$/, '')

const FR = {
  home: '/fr/',
  helpCenter: '/fr/aide/',
  blog: '/fr/blog/',
  pricing: '/fr/tarifs/',
  subtitlesTool: '/fr/outils/sous-titrer-une-video/',
  fileShareTool: '/fr/outils/envoyer-un-gros-fichier/',

  // The legal centre is one page per language with a tab per document, so a document is an
  // anchor rather than a path. The anchors are the site's tab ids, the same in both languages.
  termsOfSale: '/fr/juridique/#terms-of-sale',
  termsOfUse: '/fr/juridique/#terms-of-service',

  // Help-centre categories.
  helpGettingStarted: '/fr/aide/category/premiers-pas/',
  helpChannels: '/fr/aide/category/chaines/',

  // Help-centre articles.
  helpPlayerSettings: '/fr/aide/parametres-lecteur-videas/',
  helpRestoreGettingStarted: '/fr/aide/relancer-les-premiers-pas/',
  helpShareLink: '/fr/aide/partager-video-lien/',
  helpAutomaticSubtitles: '/fr/aide/sous-titres-automatiques-scribe/',
  helpCreateChannel: '/fr/aide/creer-chaine-video/',
  helpCustomizeChannel: '/fr/aide/personnaliser-chaine-video/',
  helpPageBuilder: '/fr/aide/page-builder-chaine/',
  helpCategoriesAndMenus: '/fr/aide/categories-menus-chaine/',
  helpChannelAccess: '/fr/aide/configurer-inscription-chaine/',
  helpCustomDomain: '/fr/aide/domaine-personnalise/',
} as const

/** A page of the corporate site the app links to. */
export type VideasPage = keyof typeof FR

const EN = {
  home: '/en/',
  helpCenter: '/en/helpdesk/',
  blog: '/en/blog/',
  pricing: '/en/pricing/',
  subtitlesTool: '/en/tools/subtitles/',
  fileShareTool: '/en/tools/share/',
  termsOfSale: '/en/legal/#terms-of-sale',
  termsOfUse: '/en/legal/#terms-of-service',

  helpGettingStarted: '/en/helpdesk/category/getting-started/',
  helpChannels: '/en/helpdesk/category/channels/',

  helpPlayerSettings: '/en/helpdesk/video-player-settings/',
  helpRestoreGettingStarted: '/en/helpdesk/restore-getting-started-steps/',
  helpShareLink: '/en/helpdesk/share-video-link/',
  helpAutomaticSubtitles: '/en/helpdesk/automatic-subtitles/',
  helpCreateChannel: '/en/helpdesk/create-video-channel/',
  helpCustomizeChannel: '/en/helpdesk/customize-video-channel/',
  helpPageBuilder: '/en/helpdesk/page-builder/',
  helpCategoriesAndMenus: '/en/helpdesk/categories-and-menus/',
  helpChannelAccess: '/en/helpdesk/channel-access-and-signup/',
  helpCustomDomain: '/en/helpdesk/custom-domain/',
} as const satisfies Record<VideasPage, string>

/** The corporate site's pages, by language: one list each, the same keys in both. */
export const VIDEAS_PAGES: Record<VideasLinkLocale, Record<VideasPage, string>> = { fr: FR, en: EN }

/**
 * Demo videos the app plays in a modal — the home page's welcome video and one per empty state —
 * embedded from Videas' own library on app.videas.com, whatever the platform or the language.
 */
export const VIDEAS_DEMO_VIDEOS = {
  welcome: 'https://app.videas.com/embed/019e742d-373a-75ee-be1a-3e43b6918237/?theme=dark',
  library: 'https://app.videas.com/embed/019e7415-bfe7-779d-a604-77a7fc7d9e24/?theme=dark',
  channels: 'https://app.videas.com/embed/019e7440-f999-7614-a9ca-d621411424a9/?theme=dark',
  scribe:
    'https://app.videas.com/embed/019e73c3-6447-762c-a738-2a57833d52f0/?show_logo=0&theme=dark&logo_position=control_bar&primary_color=884aff',
} as const

/**
 * The pictures that stand for a demo video before it is opened. `/images/media/{uid}/…` is the
 * previous platform's public thumbnail address, which `videas-images` answers too, so it outlives
 * the move of app.videas.com.
 */
export const VIDEAS_DEMO_POSTERS = {
  welcome: 'https://app.videas.com/images/media/01a1117d-f870-7145-b715-816688b1115b/thumbnail.jpg',
} as const

/** The site's language for an app locale: English for `en` and `en-*`, French for anything else. */
export function videasLocale(locale: string): VideasLinkLocale {
  return locale.split('-')[0] === 'en' ? 'en' : 'fr'
}

/** The absolute URL of a page of the corporate site, in the language of an app locale. */
export function videasPageUrl(page: VideasPage, locale: string): string {
  return `${CORPORATE_ORIGIN}${VIDEAS_PAGES[videasLocale(locale)][page]}`
}

/**
 * The slug of a page in the language of an app locale — the last segment of its path, which is
 * how the site's feeds name an article (`slug`).
 */
export function videasPageSlug(page: VideasPage, locale: string): string {
  return VIDEAS_PAGES[videasLocale(locale)][page].split('/').filter(Boolean).pop() ?? ''
}

/**
 * The absolute URL of a file a feed of the corporate site names by a root-relative path (an
 * article's `thumbnail_url`); empty for an empty path.
 */
export function corporateAssetUrl(path: string | null | undefined): string {
  if (!path) return ''
  try {
    return new URL(path, `${CORPORATE_ORIGIN}/`).toString()
  } catch {
    return ''
  }
}
